import { useState } from "react";
import { Alert } from "../components/Alert";
import { BootstrapForm } from "../components/BootstrapForm";
import { BreakGlassForm } from "../components/BreakGlassForm";
import { Logo } from "../components/Logo";
import { Spinner } from "../components/Spinner";
import { SsoButton } from "../components/SsoButton";
import { idpLabel } from "../api/sso";
import { useAuth } from "../hooks/useAuth";
import { useBranding } from "../hooks/useBranding";
import { formatError } from "../utils/formatError";

/**
 * The pre-admin gate: sign in, see who you are, and either land in the app (handled by `App.tsx`
 * once `session.is_admin` is true) or get offered the bootstrap form.
 *
 * Two routes in, deliberately unequal in prominence. Single sign-on is the button; the local
 * break-glass account is a link that has to be opened, because it bypasses the organisation's own
 * sign-in and should feel like the exception it is.
 */
export function LoginScreen(): JSX.Element {
  const { branding } = useBranding();
  const {
    hasToken,
    session,
    loadingSession,
    sessionError,
    loginWithSso,
    loginWithPassword,
    logout,
    bootstrap,
    ssoProvider,
  } = useAuth();
  // The backend sign-in flow reports failure by redirecting to `/?login_error=<reason>`
  // (`backend/web_login.py`). Read once and strip from the URL, so a reload does not re-show it.
  const [loginError, setLoginError] = useState<string | null>(() => {
    const reason = new URLSearchParams(window.location.search).get("login_error");
    if (reason === null) {
      return null;
    }
    window.history.replaceState(null, "", window.location.pathname);
    return reason === "cancelled"
      ? "Sign-in was cancelled."
      : "Sign-in failed. Your account may not be permitted on this deployment.";
  });
  const [loginBusy, setLoginBusy] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [bootstrapBusy, setBootstrapBusy] = useState(false);

  const attempt = async (action: () => Promise<void>): Promise<void> => {
    setLoginBusy(true);
    setLoginError(null);
    try {
      await action();
    } catch (error) {
      setLoginError(formatError(error));
    } finally {
      setLoginBusy(false);
    }
  };

  const handleSso = (): void => {
    void attempt(loginWithSso);
  };

  const handlePassword = async (username: string, password: string): Promise<void> => {
    await attempt(() => loginWithPassword(username, password));
  };

  const handleBootstrap = async (password: string): Promise<void> => {
    setBootstrapBusy(true);
    setBootstrapError(null);
    try {
      await bootstrap(password);
    } catch (error) {
      setBootstrapError(formatError(error));
    } finally {
      setBootstrapBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <Logo branding={branding} />

        {!hasToken && (
          <>
            {ssoProvider ? (
              <SsoButton
                provider={idpLabel()}
                onClick={handleSso}
                busy={loginBusy}
                error={loginError}
              />
            ) : (
              <Alert variant="warning">Single sign-on is not configured for this deployment.</Alert>
            )}
            <BreakGlassForm
              onSubmit={handlePassword}
              busy={loginBusy}
              error={ssoProvider ? null : loginError}
            />
          </>
        )}

        {hasToken && loadingSession && <Spinner label="Checking your session…" />}

        {hasToken && !loadingSession && sessionError && (
          <>
            <Alert variant="error">Could not check your session: {formatError(sessionError)}</Alert>
            <button type="button" className="button button--secondary" onClick={logout}>
              Sign in again
            </button>
          </>
        )}

        {hasToken && !loadingSession && !sessionError && session && !session.is_admin && (
          <div className="not-admin">
            <Alert variant="warning">
              You are signed in but not an administrator of this deployment.
            </Alert>
            <p>
              If this deployment has never been claimed, you can become its first administrator
              below. If it already has an administrator, this will be refused.
            </p>
            <BootstrapForm onSubmit={handleBootstrap} busy={bootstrapBusy} error={bootstrapError} />
            <button type="button" className="button button--secondary" onClick={logout}>
              Sign in again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
